/* eslint-disable max-lines */
import { FC, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import {
  IReservationForm,
  IReservationFormSchemaV2
} from "../../../interfaces/reservations/Reservations.types";
import { editReservationsDetails } from "../../../services/Reservations.service";
import { resolveError } from "../../../utils/apiErrorMessage";
import { canOfferReconfirm } from "../../../utils/reservations/reconfirm";
import Actions from "../../bundles/Actions";
import { isTicketCanceled, TicketStatusEnum } from "../../reservations/res.enum";
import Button from "../../ui/Button";
import useCan from "../../users/permissions/useCan";
import useSuperAdmin from "../../users/permissions/useSuperAdmin";
import useSuperAdminOrSupport from "../../users/permissions/useSuperAdminOrSupport";
import CancellationDialog from "../cancellation/CancellationDialog";
import ReconfirmAction from "../cancellation/ReconfirmAction";
import DisputeBanner from "../DisputeBanner";
import PaymentInfo from "../PaymentInfo";
import RefundStatusList from "../RefundStatusList";
import UsersInfo from "../UsersInfo";
import GeneralInfo from "./GeneralInfo";
import SelectOpenReturnAction from "./SelectOpenReturnAction";
import TripInfo from "./TripInfo";

const ReservationForm: FC<IReservationForm> = ({ details, onSelected, breakdown }) => {
  const navigate = useNavigate();
  const { handleSubmit, reset } = useFormContext<IReservationFormSchemaV2>();
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // Which ticket's "Anulo biletën" opened the dialog; null while closed.
  const [cancelTicketId, setCancelTicketId] = useState<number | null>(null);
  const [cancelBooking, setCancelBooking] = useState(false);
  // The cancellation endpoints check reservation.old.ticket.create and admit SuperAdmin or
  // CustomerSupport; deciding a lost dispute's operator payment is SuperAdmin only.
  const hasCancelPermission = useCan("reservation.old.ticket.create");
  const isSuperAdminOrSupport = useSuperAdminOrSupport();
  const isSuperAdmin = !!useSuperAdmin();
  const canCancel = hasCancelPermission && isSuperAdminOrSupport;
  // As "Anulo rezervimin": the backend cancels tickets of a confirmed booking only.
  const canCancelTicket = canCancel && details.paymentStatus === TicketStatusEnum.Confirmed;
  const canDecideOperatorPayment = hasCancelPermission && isSuperAdmin;

  // Both legs together, because a subset only exists relative to the whole booking.
  const allTickets = [
    ...(details.oneWayTicketDetails || []),
    ...(details.roundWayTicketDetails || [])
  ];
  // Every ticket counts here, cancelled ones included: on a booking whose other tickets are
  // already gone, the last live ticket keeps its own button.
  const hasSubsetToCancel = allTickets.length > 1;
  const liveTickets = allTickets.filter((t) => !isTicketCanceled(t.statusId));
  // An active booking that has already lost a ticket — worth saying on the page, because the
  // total and the passenger count still cover the whole original booking.
  const partiallyCanceled =
    details.paymentStatus === TicketStatusEnum.Confirmed &&
    liveTickets.length < allTickets.length;

  // Navigation happens here, not inside onSubmit: FormWrapper's unsaved-changes prompt is armed
  // until the submit resolves and the reset lands.
  useEffect(() => {
    if (submitted) navigate(-1);
  }, [submitted, navigate]);

  const onSubmit = async (data: IReservationFormSchemaV2) => {
    setSaving(true);
    try {
      await editReservationsDetails({
        // Already trimmed and lowercased by ReservationSchema.
        email: data.email,
        phoneNumber: data.phoneNumber,
        // details.paymentMethodId as the fallback: a payment method the options list doesn't know
        // must survive the round-trip unchanged, and the field is read-only anyway.
        paymentMethodId: data.paymentMethod?.value ?? details.paymentMethodId,
        paymentStatus: details.paymentStatus,
        uniqueJourneyId: data.uniqueJourneyId || "",
        tickets: [
          ...data.oneWayTicketDetails.map((ow) => ({
            Id: ow.ticketId,
            tripId: ow.tripId,
            journeyDate: ow.journeyDate
          })),
          ...data.roundWayTicketDetails.map((rt) => ({
            Id: rt.ticketId,
            tripId: rt.tripId,
            journeyDate: rt.journeyDate
          }))
        ]
      });
      toast.success("Rezervimi është ndryshuar me sukses.");
      // reset(data) marks the form pristine so the unsaved-changes prompt lets the navigation
      // (in the effect above) through.
      reset(data);
      setSubmitted(true);
    } catch (err) {
      toast.error(resolveError(err, "Ndodhi një gabim gjatë ndryshimit."));
    } finally {
      setSaving(false);
    }
  };

  // react-hook-form drops the submit silently when the schema rejects.
  const onInvalid = () => toast.error("Plotësoni fushat e detyrueshme.");

  const closeCancellation = () => {
    setCancelTicketId(null);
    setCancelBooking(false);
  };

  const cancellationDone = () => {
    setCancelTicketId(null);
    setCancelBooking(false);
    onSelected?.();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit, onInvalid)}>
      <div className="h-full w-full flex flex-col gap-2 px-5 mb-6">
        <DisputeBanner hasOpenDispute={details.hasOpenDispute} disputeStatus={details.disputeStatus}
          disputeOperatorPaymentWithheld={details.disputeOperatorPaymentWithheld}
          uniqueJourneyId={details.uniqueJourneyId} canDecideOperatorPayment={canDecideOperatorPayment}
          onOperatorPaymentChanged={() => onSelected?.()} />
        <div className="flex justify-between">
          <UsersInfo
            journeyGuid={details.journeyGuid}
            paymentStatus={details.paymentStatus}
            uniqueJourneyId={details.uniqueJourneyId}
          />
          <PaymentInfo
            paymentMethodId={details.paymentMethodId}
            paymentStatus={details.paymentStatus}
            price={details.price}
            breakdown={breakdown}
            clientNames={details.clientNames}
            conciergeName={details.conciergeName}
            flexType={details.flexType}
            hasTextMeMyTrip={details.hasTextMeMyTrip}
            hasOpenReturn={details.hasOpenReturn}
            hasLuggage={details.hasLuggage}
            uniqueJourneyId={details.uniqueJourneyId}
            onLuggageChange={onSelected}
            onPassengersChange={onSelected}
            cancellationSource={details.cancellationSource}
            partiallyCanceled={partiallyCanceled}
          />
        </div>
        <div className="col-start-1 lg:col-span-6 col-span-full ">
          <TripInfo
            ticketDetailsFor="oneWayTicketDetails"
            showReturn={false}
            invoiceOnlyCancellations={details.invoiceOnlyCancellations || []}
            onCancelTicket={canCancelTicket ? setCancelTicketId : undefined}
            canCancelSubset={hasSubsetToCancel}
          />
          <TripInfo
            ticketDetailsFor="roundWayTicketDetails"
            showReturn={true}
            invoiceOnlyCancellations={details.invoiceOnlyCancellations || []}
            onCancelTicket={canCancelTicket ? setCancelTicketId : undefined}
            canCancelSubset={hasSubsetToCancel}
          />
          {canCancel && (
            <div className="flex justify-end items-start gap-2 mt-2">
              {canOfferReconfirm(details) && (
                <ReconfirmAction uniqueJourneyId={details.uniqueJourneyId} onDone={cancellationDone} />
              )}
              {details.paymentStatus === TicketStatusEnum.Confirmed && (
                <Button
                  btnType="custom"
                  padding="px-2 py-1"
                  className="border border-red-500 text-red-700 text-xs"
                  onClick={() => setCancelBooking(true)}
                >
                  Anulo rezervimin
                </Button>
              )}
            </div>
          )}
          <RefundStatusList refunds={details.refunds} outsideTravel={details.refundsOutsideTravel} />
          {(cancelTicketId !== null || cancelBooking) && (
            <CancellationDialog
              booking={details}
              mode={cancelBooking ? "booking" : "ticket"}
              initialTicketId={cancelTicketId ?? undefined}
              tickets={liveTickets}
              onClose={closeCancellation}
              onDone={cancellationDone}
            />
          )}
          {details.hasOpenReturn && details.openReturn ? (
            <SelectOpenReturnAction
              uniqueJourneyId={details.uniqueJourneyId}
              openReturn={details.openReturn}
              onSelected={onSelected}
            />
          ) : null}
        </div>
      </div>

      <GeneralInfo breakdown={breakdown} />
      <Actions
        loading={saving}
        buttonTitle="Modifiko Rezervimin"
        disabled={
          details.paymentStatus !== TicketStatusEnum.Confirmed &&
          details.paymentStatus !== TicketStatusEnum.Canceled
        }
      />
    </form>
  );
};

export default ReservationForm;
